import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Loader2, X, Tags, Layers, LineChart, Globe, Building2 } from 'lucide-react';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';

// Tier definitions — mirrors the frontend defaults so users can override the right SKUs
const PTI_TIERS = [
  { id: 'lite', name: 'Itanta Lite', defaultUsd: 100 },
  { id: 'business', name: 'Itanta Business', defaultUsd: 999 },
  { id: 'professional', name: 'Itanta Professional', defaultUsd: 2999 },
];
const CORE_TIERS = [
  { id: 'start', name: 'Itanta Start', defaultUsd: 750 },
  { id: 'grow', name: 'Itanta Grow', defaultUsd: 4500 },
  { id: 'scale', name: 'Itanta Scale', defaultUsd: 10500 },
  { id: 'infinity', name: 'Itanta Infinity', defaultUsd: 30000 },
];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'DKK'];

const emptyForm = () => ({
  name: '',
  product: 'core',
  scope: 'region',
  region_id: '',
  customer_id: '',
  currency: 'USD',
  tier_overrides: [],
  discount_pct: null,
  notes: '',
  active: true,
});

const PriceListsPage = () => {
  const { isAdmin } = useAuth();
  const { brand } = useBrand();
  const [lists, setLists] = useState([]);
  const [regions, setRegions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | list
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [productFilter, setProductFilter] = useState('');

  const rebrand = (t) => ({ ...t, name: t.name.replace(/^Itanta\s/, `${brand.tierPrefix} `) });
  const ptiTiers = PTI_TIERS.map(rebrand);
  const coreTiers = CORE_TIERS.map(rebrand);

  const load = async () => {
    setLoading(true);
    try {
      const [l, r, c] = await Promise.all([
        api.get('/price-lists', { params: productFilter ? { product: productFilter } : {} }),
        api.get('/regions'),
        api.get('/customers'),
      ]);
      setLists(l.data);
      setRegions(r.data.sort((a, b) => a.name.localeCompare(b.name)));
      setCustomers(c.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [productFilter]);

  const tiersForProduct = form.product === 'pti' ? ptiTiers : coreTiers;

  const startNew = () => {
    setForm(emptyForm());
    setEditing('new');
    setErr('');
  };
  const startEdit = (l) => {
    setForm({
      name: l.name, product: l.product, scope: l.scope,
      region_id: l.region_id || '', customer_id: l.customer_id || '',
      currency: l.currency, tier_overrides: l.tier_overrides || [],
      discount_pct: l.discount_pct, notes: l.notes || '', active: l.active,
    });
    setEditing(l);
    setErr('');
  };

  const setTierPrice = (tierId, price) => {
    const others = form.tier_overrides.filter((t) => t.tier_id !== tierId);
    if (price === '' || price === null || price === undefined) {
      setForm({ ...form, tier_overrides: others });
    } else {
      setForm({ ...form, tier_overrides: [...others, { tier_id: tierId, price: parseFloat(price) }] });
    }
  };
  const getTierPrice = (tierId) => form.tier_overrides.find((t) => t.tier_id === tierId)?.price ?? '';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const payload = {
        name: form.name,
        currency: form.currency,
        tier_overrides: form.tier_overrides,
        notes: form.notes,
        active: form.active,
      };
      if (isAdmin && form.discount_pct !== null && form.discount_pct !== '' && form.discount_pct !== undefined) {
        payload.discount_pct = parseFloat(form.discount_pct);
      }
      if (editing === 'new') {
        payload.product = form.product;
        payload.scope = form.scope;
        if (form.scope === 'region') payload.region_id = form.region_id;
        if (form.scope === 'customer') payload.customer_id = form.customer_id;
        await api.post('/price-lists', payload);
      } else {
        await api.patch(`/price-lists/${editing.id}`, payload);
      }
      setEditing(null);
      await load();
    } catch (ex) {
      setErr(errorMessage(ex, 'Failed to save price list'));
    }
    setSaving(false);
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete price list "${l.name}"?`)) return;
    try { await api.delete(`/price-lists/${l.id}`); load(); } catch (e) { alert(errorMessage(e, 'Failed')); }
  };

  const getRegion = (id) => regions.find((r) => r.id === id);
  const getCustomer = (id) => customers.find((c) => c.id === id);
  const fmtMoney = (v, cur) => new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD', maximumFractionDigits: 0 }).format(v);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
            <Tags className="w-3.5 h-3.5" /> Price lists
          </div>
          <h1 className="mt-2 text-3xl font-semibold text-white tracking-tight">Price lists</h1>
          <p className="mt-1 text-white/55">
            Override list prices (and discount % — admin only) per region or per customer. Customer-scoped lists win over region-scoped lists.
          </p>
        </div>
        <button onClick={startNew} className="h-10 px-4 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> New price list
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/50">Filter by product</span>
        <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
          className="h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13px] focus:outline-none focus:border-cyan-400/50">
          <option value="">All products</option>
          <option value="pti">PTI</option>
          <option value="core">Core</option>
        </select>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-white/10 text-left text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50">
              <th className="px-6 py-4">Name</th>
              <th className="px-4 py-4">Product</th>
              <th className="px-4 py-4">Scope</th>
              <th className="px-4 py-4">Currency</th>
              <th className="px-4 py-4">Overrides</th>
              <th className="px-4 py-4">Discount</th>
              <th className="px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-white/40">Loading…</td></tr>
            ) : lists.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-white/40">No price lists yet — click “New price list” to create one.</td></tr>
            ) : lists.map((l) => (
              <tr key={l.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]">
                <td className="px-6 py-4">
                  <div className="font-medium text-white">{l.name}</div>
                  {l.notes && <div className="text-[12px] text-white/45 max-w-xs truncate">{l.notes}</div>}
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2 py-1 rounded-full border ${
                    l.product === 'pti' ? 'text-cyan-300 border-cyan-400/30 bg-cyan-400/10' : 'text-purple-300 border-purple-400/30 bg-purple-400/10'
                  }`}>
                    {l.product === 'pti' ? <LineChart className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
                    {l.product.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-4">
                  {l.scope === 'region' ? (
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-white/85"><Globe className="w-3 h-3 text-cyan-300" />{getRegion(l.region_id)?.name || l.region_id}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-white/85"><Building2 className="w-3 h-3 text-purple-300" />{getCustomer(l.customer_id)?.name || l.customer_id}</span>
                  )}
                </td>
                <td className="px-4 py-4 text-white/85 font-mono text-[12px]">{l.currency}</td>
                <td className="px-4 py-4 text-white/75">
                  {l.tier_overrides?.length > 0 ? (
                    <span className="text-[12px]">{l.tier_overrides.length} tier{l.tier_overrides.length > 1 ? 's' : ''}</span>
                  ) : <span className="text-white/40 text-[12px]">—</span>}
                </td>
                <td className="px-4 py-4 text-white/85">
                  {l.discount_pct != null ? <span className="text-emerald-300 font-semibold">{(l.discount_pct * 100).toFixed(0)}%</span> : <span className="text-white/40">—</span>}
                </td>
                <td className="px-4 py-4 text-right space-x-2">
                  <button onClick={() => startEdit(l)} className="text-[13px] text-cyan-300 hover:text-cyan-200 font-semibold">Edit</button>
                  {isAdmin && (
                    <button onClick={() => remove(l)} className="h-8 w-8 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-400/10 inline-flex items-center justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start md:items-center justify-center px-4 py-6 overflow-y-auto">
          <form onSubmit={submit} className="w-full max-w-3xl rounded-2xl border border-white/10 bg-[#12121a] p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="text-white font-semibold text-lg">
                {editing === 'new' ? 'New price list' : `Edit · ${editing.name}`}
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block md:col-span-2">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Name</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. India — Core (INR) — Enterprise Segment"
                  className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]" />
              </label>

              {editing === 'new' && (
                <>
                  <div>
                    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Product</span>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {['pti', 'core'].map((p) => (
                        <button key={p} type="button" onClick={() => setForm({ ...form, product: p, tier_overrides: [] })}
                          className={`h-10 rounded-lg border text-[13px] font-semibold transition-colors ${
                            form.product === p ? 'border-emerald-400/60 bg-emerald-400/[0.06] text-white' : 'border-white/10 bg-white/[0.02] text-white/70 hover:border-white/20'
                          }`}>{p.toUpperCase()}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Scope</span>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {['region', 'customer'].map((s) => (
                        <button key={s} type="button" onClick={() => setForm({ ...form, scope: s })}
                          className={`h-10 rounded-lg border text-[13px] font-semibold transition-colors capitalize ${
                            form.scope === s ? 'border-emerald-400/60 bg-emerald-400/[0.06] text-white' : 'border-white/10 bg-white/[0.02] text-white/70 hover:border-white/20'
                          }`}>{s}</button>
                      ))}
                    </div>
                  </div>
                  {form.scope === 'region' ? (
                    <label className="block md:col-span-2">
                      <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Region</span>
                      <select required value={form.region_id} onChange={(e) => setForm({ ...form, region_id: e.target.value })}
                        className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]">
                        <option value="">Select region</option>
                        {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </label>
                  ) : (
                    <label className="block md:col-span-2">
                      <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Customer</span>
                      <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                        className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]">
                        <option value="">Select customer</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </label>
                  )}
                </>
              )}

              <label className="block">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Currency</span>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              {isAdmin && (
                <label className="block">
                  <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Annual discount % (admin)</span>
                  <input type="number" min="0" max="100" step="1"
                    value={form.discount_pct != null ? (form.discount_pct * 100) : ''}
                    onChange={(e) => setForm({ ...form, discount_pct: e.target.value === '' ? null : parseFloat(e.target.value) / 100 })}
                    placeholder="e.g. 15"
                    className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]" />
                </label>
              )}
            </div>

            {/* Tier overrides */}
            <div className="mt-6">
              <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-3">
                Tier price overrides <span className="text-white/40 normal-case tracking-normal">— leave blank to keep the default</span>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
                {tiersForProduct.map((t) => (
                  <div key={t.id} className="flex items-center gap-4 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium text-[14px]">{t.name}</div>
                      <div className="text-[12px] text-white/45">Default {fmtMoney(t.defaultUsd, 'USD')} · overriding in <span className="font-mono">{form.currency}</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] text-white/50 font-mono">{form.currency}</span>
                      <input type="number" min="0" step="1"
                        value={getTierPrice(t.id)}
                        onChange={(e) => setTierPrice(t.id, e.target.value)}
                        placeholder="—"
                        className="w-32 h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-right tabular-nums focus:outline-none focus:border-cyan-400/50 text-[14px]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <label className="block mt-5">
              <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Notes</span>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px] resize-y" />
            </label>

            <label className="mt-4 flex items-center gap-2">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4" />
              <span className="text-[13px] text-white/70">Active</span>
            </label>

            {err && <div className="mt-3 text-[13px] text-red-300">{err}</div>}
            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="h-10 px-5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={saving} className="h-10 px-5 rounded-lg bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-black font-semibold text-sm inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PriceListsPage;
